import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, Sparkles, CheckCircle2, Phone, Users, TrendingUp,
  FileText, ClipboardCheck, Shield, Lock, RefreshCw, Globe,
  Layers, BarChart3, MessageSquare, Clock, Stethoscope,
  ChevronRight, Check, X, Database, GitBranch, Mic, Brain,
  Workflow, Radio, Play, Zap,
} from 'lucide-react';
import Footer from '../components/Footer';

/* ─────────────────────────────────────────────────────────────────────────
   AGENT ROSTER (blue palette only)
   ───────────────────────────────────────────────────────────────────────── */

const AGENTS = [
  { name: 'Amy', role: 'Receptionist', img: '/assets/amy.png', team: 'front', link: '/agents/receptionist', desc: 'Answers every inbound call 24/7. Trained on 7,300+ dental calls. Books directly into your PMS.', skills: ['Inbound Calls', 'Scheduling', 'Q&A'] },
  { name: 'Leena', role: 'Schedule Optimizer', img: '/assets/leena.png', team: 'front', link: '/agents/schedule-optimizer', desc: '12-step workflow to fill open chairs and prevent no-shows. +28% scheduled production.', skills: ['Schedule Filling', 'Confirmation', 'Recall'] },
  { name: 'Stephanie', role: 'Hygiene Recall', img: '/assets/stephanie.png', team: 'front', link: '/agents/hygiene-recall', desc: 'Voice campaigns with chart-aware context. Books into the right operatory for the right provider.', skills: ['Outbound Calls', 'Reactivation', 'Booking'] },
  { name: 'Kim', role: 'Case Acceptance', img: '/assets/kim.png', team: 'front', link: '/agents/case-acceptance', desc: 'Unlimited AI follow-ups on proposed treatment plans. Lifts case acceptance by 34%.', skills: ['Treatment Pres.', 'Education', 'Financing'] },
  { name: 'Morgan', role: 'Patient Intake', img: '/assets/morgan.png', team: 'front', link: '/agents/patient-intake', desc: 'Forms, insurance cards, and medical history — completed before patients arrive.', skills: ['Forms', 'Insurance', 'Intake'] },
  { name: 'Elise', role: 'Patient Experience', img: '/assets/elise.png', team: 'front', link: '/agents/patient-experience', desc: 'Review generation, post-visit follow-ups, and NPS tracking. 4.9★ practice average.', skills: ['Follow-ups', 'Reviews', 'NPS'] },
  { name: 'Trisha', role: 'Practice Manager', img: null, team: 'front', desc: 'AI chief of staff. Morning triage, task dashboard, KPI alerts before standup.', skills: ['Ops', 'KPIs', 'Huddles'] },
  { name: 'Joseph', role: 'Fax Manager', img: '/assets/joseph.png', team: 'front', desc: 'Intelligent fax triage and routing to the right team member. No more paper stacks.', skills: ['Triage', 'Routing', 'Forms'] },
  { name: 'Amanda', role: 'Verification', img: '/assets/amanda.png', team: 'back', link: '/agents/insurance-verification', desc: 'Procedure-level breakdowns. Catches coverage issues days in advance — written into your PMS.', skills: ['Verification', 'Benefits', 'Eligibility'] },
  { name: 'Laura', role: 'Insurance Calls', img: 'https://framerusercontent.com/images/8s85NGEzWU5LVKALaMlopNKSHg.png', team: 'back', link: '/agents/insurance-calls', desc: 'AI phone calls to payers. Navigates IVRs and speaks to representatives.', skills: ['Outbound', 'IVR Nav', 'Status'] },
  { name: 'Ariel', role: 'Claim Submission', img: '/assets/ariel.png', team: 'back', link: '/agents/claim-submission', desc: 'Auto-submits clean claims to the clearinghouse and tracks every status update.', skills: ['Claims', 'Submission', 'Coding'] },
  { name: 'Alayna', role: 'Claim Scrubbing', img: 'https://framerusercontent.com/images/gpvxkPXtZHSDq7LbDrvpyEwKzAk.png', team: 'back', link: '/agents/claim-scrubbing', desc: 'Scrubs every claim pre-submission. 97% clean-claim rate across active practices.', skills: ['Scrubbing', 'Validation', 'CDT'] },
  { name: 'Sarah', role: 'Denial Mgmt', img: '/assets/sarah.png', team: 'back', link: '/agents/denial-management', desc: 'Multi-step reasoning across 16+ data points. One-tap auto-appeal generation.', skills: ['Appeals', 'Resubmission', 'Negotiation'] },
  { name: 'Kianna', role: 'Patient Billing', img: '/assets/kianna.png', team: 'back', link: '/agents/patient-billing', desc: 'Voice and text billing campaigns. Gentle, persistent, effective — your team stays out of it.', skills: ['Statements', 'Payments', 'Inquiries'] },
  { name: 'Julia', role: 'A/R Collections', img: '/assets/julia.png', team: 'back', link: '/agents/ar-collections', desc: 'Recovers aged receivables through multi-channel outreach with chart-aware context.', skills: ['Recovery', 'Outreach', 'Aging'] },
  { name: 'Alisha', role: 'EOB Posting', img: '/assets/alisha.png', team: 'back', link: '/agents/eob-posting', desc: 'Posts ERAs and EOBs overnight. Your A/R starts the day at zero.', skills: ['Posting', 'Reconciliation', 'ERA'] },
];

/* ─────────────────────────────────────────────────────────────────────────
   HEADER
   ───────────────────────────────────────────────────────────────────────── */

const LightHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled
        ? 'bg-white/85 backdrop-blur-xl border-b border-black/[0.05] shadow-[0_1px_3px_rgba(0,0,0,0.04)]'
        : 'bg-transparent border-b border-transparent'
    }`}>
      <nav className="max-w-7xl mx-auto px-6 h-[68px] flex items-center justify-between">
        <Link to="/alt3" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-[0_4px_14px_-2px_rgba(37,99,235,0.4)]">
            <span className="text-white font-bold text-sm">A</span>
          </div>
          <span className="font-semibold text-base text-gray-900 tracking-tight">Asha</span>
          <span className="hidden sm:inline-flex items-center gap-1 ml-1 px-1.5 py-0.5 rounded-md bg-gray-100 border border-gray-200/60 text-[10px] font-mono font-medium text-gray-600 uppercase tracking-wider">AI OS</span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {[
            { label: 'Platform', href: '#platform' },
            { label: 'Workforce', href: '#workforce' },
            { label: 'ARIA', href: '#aria' },
            { label: 'Deploy', href: '#deploy' },
          ].map(item => (
            <a
              key={item.label}
              href={item.href}
              className="px-3 py-2 text-[13.5px] font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              {item.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Link to="/book-demo" className="hidden sm:inline-flex text-[13.5px] font-medium text-gray-700 hover:text-gray-900 px-3 py-2 transition-colors">
            Sign in
          </Link>
          <Link
            to="/book-demo"
            className="group inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gray-900 text-white text-[13.5px] font-semibold hover:bg-black transition-all shadow-sm hover:shadow"
          >
            Book a demo
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      </nav>
    </header>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   Calm decorative backgrounds
   ───────────────────────────────────────────────────────────────────────── */

const SoftGlow = ({ className = '' }) => (
  <div className={`absolute inset-0 pointer-events-none overflow-hidden ${className}`}>
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] rounded-full bg-blue-100/40 blur-[120px]" />
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────
   HERO — messaging + AgentsSection-style agent showcase
   ───────────────────────────────────────────────────────────────────────── */

const HeroAlt3 = () => {
  const [tab, setTab] = useState('front');
  const agents = AGENTS.filter(a => a.team === tab);

  return (
    <section id="workforce" className="relative overflow-hidden bg-white pt-[68px]">
      <SoftGlow />

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-20 pb-24">
        {/* Top messaging */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
            className="flex justify-center mb-7"
          >
            <a href="#aria" className="group inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full bg-white border border-gray-200/80 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.06)] transition-all">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-bold uppercase tracking-wider">
                <span className="w-1 h-1 rounded-full bg-white/90 animate-pulse" />
                New
              </span>
              <span className="text-[13px] font-medium text-gray-700">
                ARIA-2 · the world's first dental reasoning model
              </span>
              <ChevronRight className="w-3 h-3 text-gray-400 group-hover:translate-x-0.5 transition-transform" />
            </a>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }}
            className="text-[42px] md:text-[60px] lg:text-[72px] font-semibold leading-[1.02] tracking-[-0.035em] text-gray-900"
          >
            The AI Operating System
            <br />
            <span className="bg-gradient-to-r from-blue-500 via-blue-600 to-blue-800 bg-clip-text text-transparent">
              for dental practices.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }}
            className="mt-6 text-[17px] md:text-[19px] text-gray-600 leading-[1.55] max-w-2xl mx-auto"
          >
            16+ AI employees, dispatched by a dental-trained reasoning model. Layer onto
            Dentrix, Eaglesoft, or Open Dental — or migrate to the world's first AI-native PMS.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }}
            className="mt-9 flex flex-wrap items-center justify-center gap-3"
          >
            <Link
              to="/book-demo"
              className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-gray-900 text-white font-semibold text-[14.5px] hover:bg-black transition-all shadow-[0_8px_24px_-6px_rgba(15,23,42,0.5)]"
            >
              Book a demo
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <a
              href="#platform"
              className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-gray-200 text-gray-900 font-semibold text-[14.5px] hover:border-gray-300 hover:shadow-sm transition-all"
            >
              <Play className="w-3 h-3 fill-current" />
              See how it works
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.32 }}
            className="mt-6 flex items-center justify-center gap-5 text-[12px] text-gray-400"
          >
            <span className="inline-flex items-center gap-1.5"><Shield className="w-3 h-3" /> HIPAA · SOC2</span>
            <span className="w-1 h-1 rounded-full bg-gray-300" />
            <span>4-week go-live</span>
            <span className="w-1 h-1 rounded-full bg-gray-300" />
            <span>Net-positive month 1</span>
          </motion.div>
        </div>

        {/* Agents showcase — AgentsSection layout */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200/70 text-blue-700 text-[12px] font-semibold mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Digital Workforce</span>
            </div>
            <h2 className="text-[30px] md:text-[40px] font-semibold tracking-[-0.025em] leading-[1.1] text-gray-900">
              The only platform with a <span className="text-blue-600">full team of 16+ dental AI agents</span> for every use case
            </h2>
            <p className="mt-4 text-[15px] md:text-[16.5px] text-gray-600 leading-relaxed">
              We're the only company in the world that ships a full team of AI agents that work together — coordinating across calls, scheduling, claims, billing, and recall — to automate your entire front and back office.
            </p>
          </div>

          {/* Tab Switcher */}
          <div className="bg-gray-100 p-1 rounded-full inline-flex flex-shrink-0 relative">
            <motion.div
              className="absolute top-1 bottom-1 rounded-full bg-white shadow-sm z-0"
              initial={false}
              animate={{ left: tab === 'front' ? '4px' : '50%', width: 'calc(50% - 6px)', x: tab === 'front' ? 0 : 2 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            />
            {[{ id: 'front', label: 'Front Office' }, { id: 'back', label: 'Revenue Cycle' }].map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-6 py-2.5 rounded-full text-[13.5px] font-semibold transition-colors duration-200 relative z-10 ${
                  tab === t.id ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Agent cards grid */}
        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {agents.map(agent => {
              const CardInner = () => (
                <>
                  <div className="p-6 pb-0 flex items-start justify-between">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-2xl overflow-hidden ring-1 ring-gray-100">
                        {agent.img ? (
                          <img src={agent.img} alt={agent.name} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-700 text-white text-3xl font-bold">
                            {agent.name[0]}
                          </div>
                        )}
                      </div>
                      <div className="absolute -bottom-1 -right-1 bg-emerald-500 border-[3px] border-white w-6 h-6 rounded-full" />
                    </div>
                    {agent.link && (
                      <div className="w-8 h-8 rounded-full bg-gray-50 group-hover:bg-blue-600 flex items-center justify-center transition-all duration-300">
                        <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-white transition-colors" />
                      </div>
                    )}
                  </div>
                  <div className="p-6 pt-5 flex-1 flex flex-col">
                    <div className="mb-3">
                      <h3 className="text-[19px] font-bold text-gray-900 mb-0.5">{agent.name}</h3>
                      <p className="text-[12px] font-semibold text-blue-600 uppercase tracking-wider">{agent.role}</p>
                    </div>
                    <p className="text-gray-500 text-[13.5px] leading-relaxed mb-4 flex-1">{agent.desc}</p>
                    <div className="flex flex-wrap gap-1.5 mt-auto">
                      {agent.skills?.map((skill, i) => (
                        <span key={i} className="px-2.5 py-1 rounded-full bg-gray-50 text-gray-500 text-[10.5px] font-semibold border border-gray-100 uppercase tracking-wider">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </>
              );

              return (
                <motion.div
                  key={agent.name + agent.role}
                  layout
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.22 }}
                >
                  {agent.link ? (
                    <Link to={agent.link} className="group relative bg-white rounded-2xl border border-gray-200/80 hover:border-gray-300 hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col h-full">
                      <CardInner />
                    </Link>
                  ) : (
                    <div className="group relative bg-white rounded-2xl border border-gray-200/80 hover:border-gray-300 hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col h-full">
                      <CardInner />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>

        {/* Trust strip */}
        <motion.div
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          className="mt-20 pt-8 border-t border-gray-100"
        >
          <div className="text-center text-[10.5px] uppercase tracking-[0.2em] text-gray-400 mb-5 font-semibold">
            trusted by practices across the country
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-x-6 gap-y-4 items-center">
            {['Pacific Dental', 'Harbor Smiles', 'Northside DDS', 'Bluebird Dental', 'Oakwood Family', 'Summit Dental'].map(n => (
              <div key={n} className="text-center text-[12px] font-semibold tracking-tight text-gray-400 hover:text-gray-700 transition-colors">{n}</div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   SECTION HEADING — used everywhere
   ───────────────────────────────────────────────────────────────────────── */

const SectionHeading = ({ eyebrow, title, sub, align = 'center' }) => (
  <div className={`max-w-3xl ${align === 'center' ? 'mx-auto text-center' : ''}`}>
    {eyebrow && (
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200/70 text-blue-700 text-[10.5px] font-mono uppercase tracking-[0.18em] mb-5">
        {eyebrow}
      </div>
    )}
    <h2 className="text-[34px] md:text-[44px] font-semibold tracking-[-0.025em] leading-[1.08] text-gray-900">
      {title}
    </h2>
    {sub && <p className="mt-5 text-[16.5px] text-gray-600 leading-relaxed">{sub}</p>}
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────
   PLATFORM — simple 5-piece content
   ───────────────────────────────────────────────────────────────────────── */

const PlatformSection = () => {
  const pieces = [
    {
      icon: Mic,
      label: 'Operator',
      title: 'Ask in plain English',
      desc: '“Run verifications for tomorrow” → dispatched in seconds. Voice, chat, desktop, or mobile.',
    },
    {
      icon: Brain,
      label: 'ARIA',
      title: 'A dental reasoning model',
      desc: 'Trained on dental admin workflows, CDT codes, and 16+ payer policies. Routes intent to the right agent.',
    },
    {
      icon: Users,
      label: 'Workforce',
      title: '20+ specialized AI employees',
      desc: 'Reception, scheduling, recall, verification, claims, denials, billing, A/R — coordinated through shared state.',
    },
    {
      icon: Radio,
      label: 'Comms',
      title: 'AI-native communication',
      desc: 'VoIP, SMS, intelligent fax, universal inbox, team chat — channels your agents can act on directly.',
    },
    {
      icon: Database,
      label: 'Data',
      title: 'PMS — yours or ours',
      desc: 'Run on Asha PMS, or layer onto Dentrix, Eaglesoft, Open Dental. Same agents either way.',
    },
  ];

  return (
    <section id="platform" className="relative bg-[#FAFAFB] py-28">
      <div className="relative max-w-6xl mx-auto px-6">
        <SectionHeading
          eyebrow={<><Layers className="w-3 h-3" /> the platform</>}
          title="One operating system. Every layer of your practice."
          sub="Asha is built like an OS, not a tool. Five layers, one platform, every department on autopilot."
        />

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pieces.map((p, i) => {
            const I = p.icon;
            return (
              <motion.div
                key={p.label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ delay: i * 0.05 }}
                className="rounded-2xl border border-gray-200/80 bg-white p-6 hover:border-gray-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                    <I className="w-4.5 h-4.5 text-blue-700" strokeWidth={1.75} />
                  </div>
                  <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-gray-400">{p.label}</span>
                </div>
                <h3 className="text-[16.5px] font-semibold text-gray-900 tracking-tight">{p.title}</h3>
                <p className="mt-1.5 text-[13.5px] text-gray-600 leading-relaxed">{p.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   TWO PATHS — simple toggle, text-forward
   ───────────────────────────────────────────────────────────────────────── */

const TwoPathsAlt3 = () => {
  const [active, setActive] = useState('layer');

  const layer = {
    eyebrow: 'path 01 · layer',
    title: 'Keep your PMS. Add the AI workforce.',
    body: 'Don\'t rip and replace. Our AI agents connect to Dentrix, Eaglesoft, or Open Dental — and start working on day one.',
    bullets: [
      { i: Zap, t: 'Go-live in 2–3 weeks', s: 'No data migration. No retraining.' },
      { i: Lock, t: 'Read/write to your PMS', s: 'Direct connectors — not screen scraping.' },
      { i: Workflow, t: 'Same agents. Same ARIA model.', s: 'Full workforce, layered on top.' },
    ],
    link: { to: '/integrations', label: 'See supported PMS integrations' },
    pms: ['Dentrix', 'Eaglesoft', 'Open Dental', 'Curve', 'Carestack', 'Denticon'],
  };

  const migrate = {
    eyebrow: 'path 02 · migrate',
    title: 'Migrate to Asha PMS. Unify everything.',
    body: 'When you\'re ready, migrate to the world\'s first AI-native dental PMS. Charts, perio, treatment planning, scheduling, ledgers, imaging — all in one cloud platform.',
    bullets: [
      { i: Layers, t: 'Replace 10+ softwares', s: 'PMS + VoIP + verification + claims + imaging.' },
      { i: Stethoscope, t: 'Clinical AI built in', s: 'AI Scribe · AI Treatment Planner · AI Copilot.' },
      { i: Shield, t: 'White-glove 4-week go-live', s: 'Full migration, QA, training. Zero downtime.' },
    ],
    link: { to: '/integrations', label: 'Plan your migration' },
    replaced: ['Dentrix', 'Weave', 'eAssist', 'Dentina', 'Vyne', 'Pearl AI'],
  };

  const data = active === 'layer' ? layer : migrate;

  return (
    <section id="deploy" className="relative bg-white py-28">
      <div className="relative max-w-6xl mx-auto px-6">
        <SectionHeading
          eyebrow={<><GitBranch className="w-3 h-3" /> deployment paths</>}
          title="Two ways to deploy. Zero compromise on AI."
          sub="Keep your current PMS and add the AI workforce. Or migrate to the world's first AI-native PMS. You choose."
        />

        <div className="mt-10 flex justify-center mb-10">
          <div className="inline-flex items-center p-1 rounded-full bg-gray-100 border border-gray-200/80">
            {[
              { id: 'layer', label: 'Layer on your PMS' },
              { id: 'migrate', label: 'Migrate to Asha PMS' },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                className={`relative px-5 py-2.5 rounded-full text-[13.5px] font-semibold transition-colors ${
                  active === t.id ? 'text-white' : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                {active === t.id && (
                  <motion.div
                    layoutId="deploy-pill-light"
                    className="absolute inset-0 bg-gray-900 rounded-full"
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative">{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="grid lg:grid-cols-12 gap-8 items-start"
          >
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-blue-50 border border-blue-200/70 text-blue-700 text-[10px] font-mono uppercase tracking-[0.16em] mb-4">
                {data.eyebrow}
              </div>
              <h3 className="text-[28px] md:text-[34px] font-semibold tracking-[-0.02em] leading-[1.1] text-gray-900">
                {data.title}
              </h3>
              <p className="mt-4 text-[16px] text-gray-600 leading-relaxed max-w-xl">
                {data.body}
              </p>
              <div className="mt-7 space-y-3.5">
                {data.bullets.map((r, i) => {
                  const I = r.i;
                  return (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
                        <I className="w-4 h-4 text-blue-700" />
                      </div>
                      <div>
                        <div className="text-[14.5px] font-semibold text-gray-900">{r.t}</div>
                        <div className="text-[13px] text-gray-500">{r.s}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Link to={data.link.to} className="mt-7 inline-flex items-center gap-1.5 text-[14px] font-semibold text-blue-700 hover:text-blue-800 group">
                {data.link.label}
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>

            <div className="lg:col-span-5">
              {active === 'layer' ? (
                <div className="rounded-2xl border border-gray-200/80 bg-white p-5 shadow-[0_4px_20px_-8px_rgba(15,23,42,0.08)]">
                  <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-gray-400 mb-3">connects to</div>
                  <div className="grid grid-cols-3 gap-2">
                    {layer.pms.map(p => (
                      <div key={p} className="rounded-lg border border-gray-200/70 bg-gray-50/40 px-3 py-3 text-center">
                        <div className="text-[12.5px] font-semibold text-gray-800">{p}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-[12px] text-gray-500">
                    <span className="font-mono">+ 9 more PMS integrations</span>
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> connected
                    </span>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-gray-200/80 bg-white p-5 shadow-[0_4px_20px_-8px_rgba(15,23,42,0.08)]">
                  <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-gray-400 mb-3">consolidates</div>
                  <div className="space-y-1.5">
                    {migrate.replaced.map(o => (
                      <div key={o} className="flex items-center justify-between px-3 py-2 rounded-md bg-gray-50/50">
                        <span className="text-[13px] text-gray-500 line-through decoration-gray-300">{o}</span>
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 rounded-lg border border-blue-200/60 bg-blue-50/40 px-3 py-2.5 flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-md bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center">
                      <Sparkles className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div>
                      <div className="text-[12.5px] font-semibold text-gray-900">Asha · one unified platform</div>
                      <div className="text-[11px] text-gray-500">PMS, comms, claims, imaging, AI workforce</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};


/* ─────────────────────────────────────────────────────────────────────────
   ARIA — story showing action + memory + iterative reasoning
   ───────────────────────────────────────────────────────────────────────── */

const ARIASectionAlt3 = () => (
  <section id="aria" className="relative bg-white py-28">
    <div className="relative max-w-6xl mx-auto px-6">
      <div className="grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200/70 text-blue-700 text-[11.5px] font-semibold mb-5">
            <Brain className="w-3.5 h-3.5" /> The reasoning kernel
          </div>
          <h2 className="text-[34px] md:text-[44px] font-semibold tracking-[-0.025em] leading-[1.08] text-gray-900">
            Most “AI” answers questions.
            <br />
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 bg-clip-text text-transparent">
              ARIA gets things done.
            </span>
          </h2>
          <p className="mt-5 text-[16.5px] text-gray-600 leading-relaxed">
            ARIA doesn't reply — it acts, remembers, and iterates. It picks up the phone, reads the chart, calls the payer, drafts the appeal, follows up next week, and keeps the patient relationship intact across every touchpoint.
          </p>
          <div className="mt-8 space-y-4">
            {[
              { t: 'Takes real action', s: 'Picks up calls, books appointments, files appeals, posts payments — autonomously.' },
              { t: 'Patient memory', s: 'Every conversation, claim, and chart note is recalled the next time the patient is touched.' },
              { t: 'Builds intelligent context', s: 'Checks benefits with the clearinghouse, reads clinical history, references payer policy — before reaching out.' },
              { t: 'Iterates across touchpoints', s: 'Follows up days or weeks later, picking up exactly where the last interaction left off.' },
            ].map((r, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-3.5 h-3.5 text-blue-700" />
                </div>
                <div>
                  <div className="text-[14.5px] font-semibold text-gray-900">{r.t}</div>
                  <div className="text-[13px] text-gray-500 leading-relaxed">{r.s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <StoryTimeline />
        </div>
      </div>
    </div>
  </section>
);

const StoryTimeline = () => {
  const frames = [
    {
      kind: 'call',
      day: 'Day 1 · 2:14 PM',
      who: 'Stephanie · Hygiene Recall',
      img: '/assets/stephanie.png',
      messages: [
        { from: 'agent', text: 'Hi Sarah, this is Stephanie from Dr. Park\'s office calling about scheduling your cleaning.' },
        { from: 'patient', text: 'Oh hey — I\'m actually on vacation in Italy until next week. Can you call me back?' },
        { from: 'agent', text: 'Of course, have a great trip. I\'ll reach out when you\'re back.' },
      ],
    },
    {
      kind: 'memory',
      day: 'Background · over the next 7 days',
      title: 'ARIA builds context — autonomously',
      items: [
        { i: Clock, t: 'Patient memory updated', s: 'Sarah Chen · on vacation · resume outreach 5/30.' },
        { i: Shield, t: 'Benefits checked with clearinghouse', s: 'Aetna · cleaning fully covered · $0 patient responsibility.' },
        { i: FileText, t: 'Chart history reviewed', s: 'Stage II periodontitis · last cleaning 5mo ago · recall overdue 4 weeks.' },
        { i: Workflow, t: 'Follow-up queued', s: 'Call back 5/31, reference vacation, lead with coverage + clinical need.' },
      ],
    },
    {
      kind: 'call',
      day: 'Day 8 · 10:22 AM',
      who: 'Stephanie · Hygiene Recall',
      img: '/assets/stephanie.png',
      messages: [
        { from: 'agent', text: 'Hi Sarah! Hope Italy was amazing. I wanted to follow up on that cleaning — I checked your Aetna plan, it\'s fully covered. And given your periodontitis history, we really want to get you in soon.' },
        { from: 'patient', text: 'Wow, you remembered. Yeah, let\'s do it — any Tuesday afternoon works.' },
        { from: 'agent', text: 'Perfect. I have Tuesday 5/7 at 2pm with Dr. Park — texting confirmation now.' },
      ],
      done: 'Booked · operatory 3 · Dr. Park · confirmed via SMS',
    },
  ];

  return (
    <div className="space-y-4">
      {frames.map((f, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ delay: i * 0.08, duration: 0.4 }}
        >
          {f.kind === 'call' ? <CallCard frame={f} /> : <MemoryCard frame={f} />}
        </motion.div>
      ))}
    </div>
  );
};

const CallCard = ({ frame }) => (
  <div className="rounded-2xl border border-gray-200/80 bg-white shadow-[0_4px_20px_-8px_rgba(15,23,42,0.08)] overflow-hidden">
    <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 bg-gray-50/40">
      <div className="flex items-center gap-2.5">
        <img src={frame.img} alt="" className="w-7 h-7 rounded-lg object-cover ring-1 ring-gray-200" />
        <div>
          <div className="text-[12.5px] font-semibold text-gray-900">{frame.who}</div>
          <div className="text-[10.5px] text-gray-500">{frame.day}</div>
        </div>
      </div>
      <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-semibold uppercase tracking-wider border border-blue-100">
        <Phone className="w-2.5 h-2.5" /> Voice call
      </div>
    </div>
    <div className="p-5 space-y-2.5">
      {frame.messages.map((m, i) => (
        <div key={i} className={m.from === 'agent' ? 'flex justify-start' : 'flex justify-end'}>
          <div className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
            m.from === 'agent'
              ? 'bg-blue-50/70 text-gray-800 rounded-bl-sm border border-blue-100/80'
              : 'bg-gray-100 text-gray-700 rounded-br-sm'
          }`}>{m.text}</div>
        </div>
      ))}
    </div>
    {frame.done && (
      <div className="px-5 py-3 border-t border-gray-100 bg-emerald-50/40 flex items-center gap-2">
        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
        <span className="text-[12.5px] font-medium text-emerald-800">{frame.done}</span>
      </div>
    )}
  </div>
);

const MemoryCard = ({ frame }) => (
  <div className="relative rounded-2xl border border-blue-200/60 bg-gradient-to-br from-blue-50/60 via-blue-50/30 to-indigo-50/40 p-5">
    <div className="flex items-center gap-2.5 mb-4">
      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center shadow-[0_4px_12px_-4px_rgba(37,99,235,0.5)]">
        <Brain className="w-4 h-4 text-white" />
      </div>
      <div>
        <div className="text-[12.5px] font-semibold text-gray-900">{frame.title}</div>
        <div className="text-[10.5px] text-gray-500">{frame.day}</div>
      </div>
    </div>
    <div className="grid sm:grid-cols-2 gap-2.5">
      {frame.items.map((item, i) => {
        const I = item.i;
        return (
          <div key={i} className="flex items-start gap-2.5 p-3 rounded-lg bg-white/70 border border-white">
            <div className="w-6 h-6 rounded-md bg-blue-100 flex items-center justify-center shrink-0">
              <I className="w-3 h-3 text-blue-700" />
            </div>
            <div className="min-w-0">
              <div className="text-[12px] font-semibold text-gray-900">{item.t}</div>
              <div className="text-[11.5px] text-gray-500 leading-snug">{item.s}</div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────
   STATS strip
   ───────────────────────────────────────────────────────────────────────── */

const StatsAlt3 = () => {
  const stats = [
    { v: '$9.8K', l: 'avg monthly revenue lift', s: 'across active practices' },
    { v: '+34%', l: 'case acceptance', s: 'AI Treatment Planner' },
    { v: '+28%', l: 'scheduled production', s: 'Leena schedule optimizer' },
    { v: '~3 hrs', l: 'staff time / patient', s: 'manual work automated' },
  ];
  return (
    <section className="relative bg-[#FAFAFB] py-20">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-px bg-gray-200/70 rounded-2xl overflow-hidden border border-gray-200/80">
          {stats.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="bg-white px-6 py-7"
            >
              <div className="text-[32px] font-bold tracking-[-0.025em] text-gray-900 leading-none tabular-nums">{s.v}</div>
              <div className="mt-2 text-[13.5px] font-semibold text-gray-800">{s.l}</div>
              <div className="mt-0.5 text-[11.5px] text-gray-500 font-mono">{s.s}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   COMPARISON
   ───────────────────────────────────────────────────────────────────────── */

const compareRowsAlt3 = [
  { label: 'Purpose-built dental AI model (ARIA)', asha: true, chatbots: false, pms: false },
  { label: 'Autonomous multi-step reasoning', asha: true, chatbots: false, pms: false },
  { label: 'Takes action — not just answers', asha: true, chatbots: false, pms: false },
  { label: '20+ AI employees ship on day one', asha: true, chatbots: 'one-agent', pms: false },
  { label: 'Layer onto Dentrix / Eaglesoft / Open Dental', asha: true, chatbots: 'partial', pms: false },
  { label: 'Replace PMS when you\'re ready', asha: true, chatbots: false, pms: false },
  { label: 'AI-native VoIP, SMS, fax included', asha: true, chatbots: false, pms: 'addon' },
  { label: 'Built in-house — not a ChatGPT wrapper', asha: true, chatbots: false, pms: false },
];

const ComparisonAlt3 = () => (
  <section className="relative bg-white py-28">
    <div className="relative max-w-6xl mx-auto px-6">
      <SectionHeading
        eyebrow={<><GitBranch className="w-3 h-3" /> differentiation</>}
        title="Every other dental AI is a chatbot."
        sub="They wrap ChatGPT. We built an operating system. Here's what the difference looks like."
      />

      <div className="mt-12 rounded-2xl overflow-hidden border border-gray-200/80 bg-white shadow-[0_4px_20px_-8px_rgba(15,23,42,0.06)]">
        <div className="grid grid-cols-12 px-5 py-4 bg-gray-50/60 border-b border-gray-200/80 text-[10.5px] font-mono uppercase tracking-[0.16em] text-gray-500">
          <div className="col-span-6" />
          <div className="col-span-2 text-center">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-100 text-gray-900 border border-blue-200/60">
              <Sparkles className="w-2.5 h-2.5 text-blue-600" />Asha
            </span>
          </div>
          <div className="col-span-2 text-center">AI chatbots</div>
          <div className="col-span-2 text-center">Legacy PMS</div>
        </div>
        {compareRowsAlt3.map((r, i) => (
          <div key={r.label} className={`grid grid-cols-12 px-5 py-3.5 items-center text-[13.5px] ${i !== compareRowsAlt3.length - 1 ? 'border-b border-gray-100' : ''}`}>
            <div className="col-span-6 text-gray-800 font-medium">{r.label}</div>
            {[r.asha, r.chatbots, r.pms].map((v, j) => (
              <div key={j} className="col-span-2 flex justify-center">
                {v === true ? (
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${j === 0 ? 'bg-emerald-50 ring-1 ring-emerald-200/60' : 'bg-gray-50'}`}>
                    <Check className={`w-3.5 h-3.5 ${j === 0 ? 'text-emerald-600' : 'text-gray-400'}`} />
                  </div>
                ) : v === false ? (
                  <div className="w-6 h-6 rounded-full bg-gray-50 flex items-center justify-center">
                    <X className="w-3.5 h-3.5 text-gray-300" />
                  </div>
                ) : (
                  <span className="text-[10px] font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded border border-gray-200/70">{v}</span>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────────────────────────────────────
   SECURITY / INFRA
   ───────────────────────────────────────────────────────────────────────── */

const SecurityAlt3 = () => {
  const items = [
    { i: Lock, t: 'HIPAA + SOC2 Type II', s: 'AWS hosted. BAAs signed. Encrypted at rest + in transit.' },
    { i: RefreshCw, t: 'ARIA improves every day', s: 'Each action makes the model smarter — every practice benefits.' },
    { i: Globe, t: 'Cloud-native, no server', s: 'Skip the $15K refresh. Skip the IT contract.' },
    { i: Layers, t: 'Scales from 1 op to 50', s: 'Add a chair — ARIA picks up the extra calls. No hiring.' },
    { i: Clock, t: '4-week white-glove go-live', s: 'Migration, QA, staff training. Phased launch.' },
    { i: BarChart3, t: 'Ask-Asha analytics', s: 'Natural-language reporting. KPIs vs your production goals.' },
  ];
  return (
    <section className="relative bg-[#FAFAFB] py-28">
      <div className="max-w-7xl mx-auto px-6">
        <SectionHeading
          eyebrow={<><Shield className="w-3 h-3" /> security · infrastructure</>}
          title="Enterprise-grade. Built for dentistry."
        />
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((b, i) => {
            const I = b.i;
            return (
              <motion.div
                key={b.t}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ delay: i * 0.05 }}
                className="group rounded-2xl bg-white border border-gray-200/80 p-5 hover:border-gray-300 hover:shadow-sm transition-all"
              >
                <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center mb-4">
                  <I className="w-4.5 h-4.5 text-blue-700" strokeWidth={1.75} />
                </div>
                <h3 className="text-[15.5px] font-semibold text-gray-900 tracking-tight">{b.t}</h3>
                <p className="mt-1.5 text-[13px] text-gray-600 leading-relaxed">{b.s}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────────────
   BIG CTA
   ───────────────────────────────────────────────────────────────────────── */

const BigCTAAlt3 = () => (
  <section className="relative bg-white pb-24">
    <div className="max-w-7xl mx-auto px-6">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900 via-[#0c1730] to-[#101c44] p-10 md:p-16 border border-gray-900">
        <div className="absolute inset-0 opacity-50 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-80 h-80 bg-blue-500/30 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-indigo-500/25 rounded-full blur-3xl" />
        </div>
        <div className="relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <h2 className="text-[36px] md:text-[48px] font-semibold tracking-[-0.025em] leading-[1.05] text-white">
              See your AI workforce
              <br />
              <span className="text-white/40">running on your practice.</span>
            </h2>
            <p className="mt-5 text-[17px] text-white/60 max-w-xl leading-relaxed">
              20-minute demo. We'll route your top workflows through ARIA live and quote your exact ROI.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Link to="/book-demo" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white text-gray-900 font-semibold text-[15px] hover:bg-white/90 transition-all">
              Book your demo
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/roi-calculator" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white/[0.06] text-white font-semibold text-[15px] hover:bg-white/[0.1] transition-all border border-white/[0.12]">
              Calculate your ROI
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────────────────────────────────────
   PAGE
   ───────────────────────────────────────────────────────────────────────── */

const AlternateLanding3 = () => {
  return (
    <div className="min-h-screen bg-white">
      <LightHeader />
      <HeroAlt3 />
      <PlatformSection />
      <TwoPathsAlt3 />
      <ARIASectionAlt3 />
      <StatsAlt3 />
      <ComparisonAlt3 />
      <SecurityAlt3 />
      <BigCTAAlt3 />
      <Footer />
    </div>
  );
};

export default AlternateLanding3;
